// Cloud-Spielstand: Profil (Kneipe, Erfolge, Logbuch) und alle Kapitel-Spielstände werden mit dem Konto synchronisiert.
// Lokale Änderungen melden sich per Ereignis „dlr-local-change“; neuerer Stand gewinnt.
import { writable, get } from 'svelte/store';
import { account, getCloudSave, putCloudSave, type CloudData } from '../api/account.ts';
import { allSavesRaw, putSavesRaw } from './save.ts';

const AT = 'dlr.localAt';
export const cloudStatus = writable<{ state: 'off' | 'saving' | 'saved' | 'error'; at: number }>({ state: 'off', at: 0 });

const localAt = () => Number(safeGet(AT) ?? 0);
function safeGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* privat */
  }
}

/** lokale Änderung: Zeitstempel setzen, gebündelt hochladen */
export function markLocalChange() {
  safeSet(AT, String(Date.now()));
  dispatchEvent(new Event('dlr-local-change'));
}

async function snapshot(): Promise<CloudData> {
  const local: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)!;
      if (k.startsWith('dlr.daily.') || k === 'dlr.settings') local[k] = localStorage.getItem(k)!;
    }
  } catch {
    /* egal */
  }
  return { profile: JSON.parse(safeGet('dlr.profile') ?? 'null'), saves: await allSavesRaw(), local };
}

async function restore(d: CloudData, at: number) {
  if (d.profile) safeSet('dlr.profile', JSON.stringify(d.profile));
  for (const [k, v] of Object.entries(d.local ?? {})) safeSet(k, v);
  await putSavesRaw(d.saves ?? []);
  safeSet(AT, String(at));
}

let timer: number | undefined;
export async function push(): Promise<void> {
  if (!get(account)) return;
  cloudStatus.set({ state: 'saving', at: Date.now() });
  try {
    const at = localAt() || Date.now();
    await putCloudSave(await snapshot(), at);
    cloudStatus.set({ state: 'saved', at: Date.now() });
  } catch (e) {
    if ((e as { status?: number }).status === 409) await pull(true);
    else cloudStatus.set({ state: 'error', at: Date.now() });
  }
}

/** Stand vom Server holen; true, wenn lokal etwas ersetzt wurde (dann neu laden) */
export async function pull(force = false): Promise<boolean> {
  const s = await getCloudSave();
  if (s.data && (force || s.updatedAt > localAt())) {
    await restore(s.data, s.updatedAt);
    cloudStatus.set({ state: 'saved', at: Date.now() });
    return true;
  }
  return false;
}

/** nach dem Anmelden: neuerer Stand gewinnt; gibt true zurück, wenn der Cloud-Stand übernommen wurde */
export async function syncAfterLogin(): Promise<boolean> {
  try {
    if (await pull()) return true;
    await push();
  } catch {
    cloudStatus.set({ state: 'error', at: Date.now() });
  }
  return false;
}

export function startCloudSync() {
  addEventListener('dlr-save-written', () => markLocalChange());
  addEventListener('dlr-local-change', () => {
    if (!get(account)) return;
    clearTimeout(timer);
    timer = window.setTimeout(() => void push(), 1500);
  });
  // Seite wird verlassen: letzten Stand sofort sichern
  addEventListener('pagehide', () => {
    if (get(account) && timer) void push();
  });
}
